/**
 * @file not-found-columns.tsx
 * @description Колонки и пункты «⋯» журнала «Не найдено» для
 * `ResponsiveList` (phase19.16, партия «SEO-техника», v0.19.65; SB148,
 * SB55; макет uiux-seo-tech.md §3.1). Одно описание — две раскладки:
 * таблица с 1024 и карточка ниже (роли `compact`). Ячейки — `NotFoundRow.tsx`.
 */

import { Eye, EyeOff, ExternalLink, SearchCheck } from "lucide-react";

import type { ListColumn } from "../../ui/responsive-list-types";
import type { RowAction } from "../../ui/RowActionsMenu";
import { ActionButton, PathCell, SortHeader, StatusCell, type NotFoundListRow, type NotFoundRowHandlers } from "./NotFoundRow";
import { BOTS_HINT, botsText, lastDayText, notFoundRowAction, notFoundStatusPill, visitorsText } from "../../../lib/site-seo/not-found-view";

export function notFoundColumns(h: NotFoundRowHandlers): ListColumn<NotFoundListRow>[] {
  return [
    { key: "path", header: "Адрес", cell: (item) => <PathCell item={item} />, compact: "title" },
    {
      key: "visitors",
      header: "Посетители",
      headerNode: <SortHeader label="Посетители" value="visitors" h={h} />,
      ariaSort: h.sort === "visitors" ? "descending" : "none",
      width: "w-28",
      align: "end",
      cell: (item) => (item.kind === "other" ? item.visitors : item.row.visitors),
      compactCell: (item) => visitorsText(item.kind === "other" ? item.visitors : item.row.visitors),
      compact: "subtitle",
    },
    {
      key: "bots",
      header: "Поисковики",
      headerNode: <SortHeader label="Поисковики" value="bots" h={h} hint={BOTS_HINT} />,
      ariaSort: h.sort === "bots" ? "descending" : "none",
      width: "w-32",
      align: "end",
      cell: (item) => (item.kind === "other" ? item.bots : item.row.bots),
      compactCell: (item) => botsText(item.kind === "other" ? item.bots : item.row.bots),
      compact: "subtitle",
    },
    {
      key: "last",
      header: "Последний раз",
      width: "w-28",
      // 1024–1279: второй линией под адресом — таблица влезает в колонку кабинета.
      fold: { into: "path", cell: (item) => `последний раз ${lastDayText(item.kind === "other" ? item.lastDay : item.row.lastDay, h.today)}` },
      cell: (item) => lastDayText(item.kind === "other" ? item.lastDay : item.row.lastDay, h.today),
      compactCell: (item) => `последний раз ${lastDayText(item.kind === "other" ? item.lastDay : item.row.lastDay, h.today)}`,
      compact: "meta",
    },
    { key: "status", header: "Статус", width: "w-40", cell: (item) => <StatusCell item={item} />, compactCell: (item) => (item.kind === "other" ? null : notFoundStatusPill(item.row) ? <StatusCell item={item} /> : null), compact: "meta" },
    {
      key: "action",
      header: "Действие",
      width: "w-40",
      cell: (item) => <ActionButton item={item} h={h} />,
      compactCell: (item) => (item.kind === "path" && h.canEdit && notFoundRowAction(item.row) ? <ActionButton item={item} h={h} wide /> : null),
      compact: "action",
    },
  ];
}

/** Пункты «⋯»: проверить, открыть на сайте, скрыть / вернуть (последнее — с правом настроек). */
export function notFoundRowActions(item: NotFoundListRow, h: NotFoundRowHandlers): RowAction[] {
  if (item.kind === "other") return [];
  const row = item.row;
  const site = h.siteUrl;
  return [
    { key: "probe", label: "Проверить", icon: SearchCheck, run: () => h.onProbe(row.display) },
    ...(site ? [{ key: "open", label: "Открыть на сайте ↗", icon: ExternalLink, run: () => void window.open(`${site}${row.path}`, "_blank", "noopener,noreferrer") }] : []),
    ...(h.canEdit
      ? [
          row.hidden
            ? { key: "unhide", label: "Вернуть в список", icon: Eye, run: () => h.onUnhide(row) }
            : { key: "hide", label: "Скрыть", icon: EyeOff, run: () => h.onHide(row) },
        ]
      : []),
  ];
}

/** Ключ строки списка. */
export const notFoundRowKey = (item: NotFoundListRow): string => (item.kind === "other" ? "#other" : item.row.path);
